import { useEffect, useState } from "react";
import { getFeatureFlags } from "./api";

/**
 * Lightweight feature-flag fetcher with live reactivity.
 *
 * iter-109: previously this module cached the flag result for the
 * lifetime of the SPA session, so flipping a flag in the superadmin
 * Future tab only took effect after a hard refresh. That broke the
 * "Subscriptions OFF" toggle — the menu item kept showing.
 *
 * Now:
 *   - The fetch is still de-duplicated via a module-level promise so
 *     a single page-load only hits the backend once per refresh cycle.
 *   - resetFeatureFlagsCache() clears the cache AND notifies every
 *     mounted `useFeatureFlags` subscriber to refetch immediately.
 *   - We also expire the cache every 30s as a safety net so toggles
 *     made on another tab eventually propagate without a refresh.
 *
 * Defaults conservatively: every flag is ``false`` until the backend
 * answers — except for ``show_subscriptions`` which defaults TRUE so
 * billing UI doesn't blink off on slow connections.
 */
const DEFAULTS = {
    show_auto_pin: false,
    show_draw_takeoff: false,
    show_subscriptions: true,
    show_workgroup_chat: true,
    // iter-114: Display tab — every UI-visibility switch defaults TRUE
    // so a fresh install shows everything; superadmin flips off what
    // they don't want. Stored in the same backend `feature_flags`
    // doc, so persistence across VPS docker rebuilds is automatic.
    show_bottom_projects: true,
    show_bottom_contacts: true,
    show_bottom_leads: true,
    show_bottom_templates: true,
    show_bottom_email: true,
    show_bottom_plugins: true,
    show_top_projects: true,
    show_top_contacts: true,
    show_top_leads: true,
    show_top_templates: true,
    show_top_reports: true,
    show_top_email: true,
    show_top_plugins: true,
    show_top_settings: true,
    show_top_privacy: true,
    show_top_terms: true,
    show_dropdown_account: true,
    show_dropdown_workgroup: true,
    show_dropdown_hour_log: true,
    show_notifications: true,
};

const TTL_MS = 30_000;

let _cache = null;          // Promise<flags>
let _cachedAt = 0;          // ms timestamp when _cache was filled
const _subs = new Set();    // Set<() => void>

function load(force = false) {
    const now = Date.now();
    if (force || !_cache || now - _cachedAt > TTL_MS) {
        _cachedAt = now;
        _cache = getFeatureFlags().catch(() => ({}));
    }
    return _cache;
}

export function resetFeatureFlagsCache() {
    _cache = null;
    _cachedAt = 0;
    // Tell every mounted hook to refetch right now.
    _subs.forEach((fn) => {
        try { fn(); } catch { /* swallow */ }
    });
}

export function useFeatureFlags() {
    const [flags, setFlags] = useState(DEFAULTS);

    useEffect(() => {
        let dead = false;
        const refetch = () => {
            load(true).then((f) => {
                if (dead) return;
                setFlags({ ...DEFAULTS, ...f });
            });
        };
        // Initial fetch (re-uses cache if hot).
        load().then((f) => {
            if (dead) return;
            setFlags({ ...DEFAULTS, ...f });
        });
        _subs.add(refetch);
        return () => {
            dead = true;
            _subs.delete(refetch);
        };
    }, []);

    return flags;
}
